import { createStyles, makeUiStyles, Theme } from '../../styles/styles';
import { fontText, fontDisplay, StylesIndexInjections } from 'common-react';
import { fieldBoxShadow, focusStyle, screenOnlyStyle } from '../../styles/mixins';
import { themeColorsRgba } from '../../theme/tools/theme.utils';
import { zIndex } from '../../theme/constants';

export default makeUiStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 24,
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },

  contactMenuModal: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    backgroundColor: theme.color.greys[0],
    zIndex: zIndex.menuSubModalZIndex,
    '& $menuItemBox': {
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      '&$menuItemBoxPhone': {
        padding: '0 16px',
      },
    },
  },

  modal: {
    padding: 0,
    [theme.breakpoints.between('sm', 'md')]: {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  contactMenuModalMobile: {
    padding: 0,
  },
  popover: {
    minWidth: 328,
    borderRadius: 16,
    boxShadow: fieldBoxShadow(theme).bs4,
    overflowY: 'auto',
    overflowX: 'hidden',
    marginTop: 24,
    padding: 0,
    border: 0,
    '&.MuiPaper-root': {
      backgroundColor: theme.color.greys[0],
      color: theme.color.greys[80],
    },
  },

  button: {
    alignItems: 'center',
    display: 'flex',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 24,
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: themeColorsRgba(theme).black8,
    },
    '&:focus-visible': {
      ...focusStyle(theme),
      outlineOffset: 2,
    },
  },
  buttonActive: {
    position: 'relative',
    textShadow: '0.1px 0 currentColor, -0.1px 0 currentColor',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: -18,
      left: 2,
      width: '100%',
      height: 4,
      backgroundColor: theme.color.oranges[50],
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    }
  },

  menuItemPhoneIcon: {
    width: 24,
    height: 24,
  },

  menuItemPhoneHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 8px 8px',
    '&.Mui-disabled': {
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },

  buttonIcon: {
    color: theme.color.oranges[50],
  },

  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    border: 0,
    backgroundColor: 'transparent',
    minWidth: 'unset',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    '&.visually-hidden': {
      ...screenOnlyStyle(),
      '&:focus-visible': {
        clip: 'auto',
        clipPath: 'none',
        height: 'auto',
        width: 'auto',
        overflow: 'visible',
        ...focusStyle(theme, -2),
      },
      '&:hover': {
        backgroundColor: 'transparent',
      }
    },
  },

  visuallyHidden: {
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    width: 1,
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
  },

  modalLoginHelp: {
    padding: 0,
  },

  credentialsBox: {
    '&.Mui-disabled': {
      opacity: 1,
    },
    paddingTop: 0,
    paddingBottom: 16,
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.color.greys[15]}`,
  },

  buttonIconEnd: {
    color: theme.color.oranges[50],
    marginLeft: 'auto',
    width: 20,
    height: 20,
  },

  name: {
    color: theme.color.greys[80]
  },

  email: {
    font: fontText('normal', theme.fontSize.s, 20),
    color: theme.color.greys[40]
  },

  menuList: {
    padding: 24,
  },

  menuItem: {
    width: '100%',
    padding: 0,
    cursor: 'pointer',
    textDecoration: 'none',
    backgroundColor: 'transparent',
    borderRadius: 16,
    marginBottom: 16,
    '&:focus-visible': {
      ...focusStyle(theme, -2),
      backgroundColor: 'transparent',
    },
    '&:hover': {
      backgroundColor: theme.color.greys[15]
    },
  },

  menuItemPhone: {
    marginBottom: 0,
    padding: '2px 8px',
    width: 'fit-content',
  },

  menuItemBox: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    padding: 8,
    borderRadius: 16,
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: theme.color.greys[15]
    },
  },

  menuItemBoxPhone: {
    padding: 0,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },

  menuItemText: {
    font: fontText('normal', theme.fontSize.base, 24),
    marginLeft: 8,
  },

  hotlineInfoWrapper: {
    backgroundColor: theme.color.blues[10],
    borderRadius: 16,
    padding: 8,
    flexGrow: 1,
    marginTop: 16,
    font: fontText('normal', theme.fontSize.s, 20),
    color: theme.color.greys[80],
    [theme.breakpoints.down('sm')]: {
      margin: '12px 16px 16px',
      flexGrow: 0,
    },
  },

  hotLineTitle: {
    marginBottom: 12,
    color: theme.color.greys[60],
  },

  hotLineHoursInfo: {
    marginBottom: 4,
    font: fontDisplay('normal', theme.fontSize.base, 24),
  },

  hotLineRatesInfo: {
    display: 'block',
    marginTop: 12,
    color: theme.color.greys[60],
    maxWidth: 243,
    font: fontText('normal', theme.fontSize.s, 20),
    '&:first-letter': {
      textTransform: 'uppercase',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%'
    },
  },
  chevronRightIcon: {
    position: 'absolute',
    right: 16,
  },
  helpCenterIcon: {
    fill: theme.color.greys[80],
  },

  goBackButton: {
    '&:focus-visible': {
      ...focusStyle(theme),
      outlineOffset: 2,
    },
  },

  legalInfoMenuItem: {
    '&:focus-visible': {
      ...focusStyle(theme, -2),
      backgroundColor: 'transparent',
    },
  },
}), { name: 'ContactMenu', index: StylesIndexInjections.OTHER });
